import apiClient from './apiClient'

const JWT_STORAGE_KEY = 'stytch_session_jwt'

class AuthService {
  /**
   * Store JWT in localStorage
   */
  storeJWT(jwt) {
    if (jwt) {
      localStorage.setItem(JWT_STORAGE_KEY, jwt)
    }
  }

  /**
   * Get JWT from localStorage
   */
  getJWT() {
    return localStorage.getItem(JWT_STORAGE_KEY)
  }

  /**
   * Clear JWT from localStorage
   */
  clearJWT() {
    localStorage.removeItem(JWT_STORAGE_KEY)
  }

  /**
   * Register user login with backend
   * @param {string} jwt - Stytch session JWT
   * @returns {Promise<Object>} Backend response
   */
  async registerLogin(jwt) {
    try {
      const response = await apiClient.post('/api/v1/users/login', {
        token: jwt,
      })
      return { success: true, data: response }
    } catch (error) {
      console.error('Backend login registration failed:', error)
      return {
        success: false,
        error: error.message || 'Failed to register login with backend',
      }
    }
  }

  /**
   * Register user signup with backend
   * @param {string} jwt - Stytch session JWT
   * @returns {Promise<Object>} Backend response
   */
  async registerSignup(jwt) {
    try {
      const response = await apiClient.post('/api/v1/users/signup', {
        token: jwt,
      })
      return { success: true, data: response }
    } catch (error) {
      console.error('Backend signup registration failed:', error)
      return {
        success: false,
        error: error.message || 'Failed to register signup with backend',
      }
    }
  }

  /**
   * Handle authentication success - store JWT and register with backend
   * @param {Object} stytchResponse - Response from Stytch authentication
   * @param {boolean} isSignup - Whether this is a signup (true) or login (false)
   * @returns {Promise<Object>} Result of backend registration
   */
  async handleAuthSuccess(stytchResponse, isSignup = false) {
    try {
      // Extract JWT from Stytch response
      const jwt = stytchResponse?.session_jwt || stytchResponse?.session?.session_jwt

      if (!jwt) {
        console.error('No JWT found in Stytch response:', stytchResponse)
        return {
          success: false,
          error: 'No session token received from authentication',
        }
      }

      // Store JWT in localStorage
      this.storeJWT(jwt)

      // Register with backend
      const backendResult = isSignup ? await this.registerSignup(jwt) : await this.registerLogin(jwt)

      if (!backendResult.success) {
        console.warn('Backend registration failed, but local auth succeeded')
      }

      return backendResult
    } catch (error) {
      console.error('Error handling auth success:', error)
      return {
        success: false,
        error: error.message || 'Failed to complete authentication',
      }
    }
  }

  /**
   * Handle OAuth authentication - try login first, fallback to signup if user is new
   * @param {Object} stytchResponse - Response from Stytch OAuth authentication
   * @returns {Promise<Object>} Result of backend registration
   */
  async handleOAuthSuccess(stytchResponse) {
    try {
      // Extract JWT from Stytch response
      const jwt = stytchResponse?.session_jwt || stytchResponse?.session?.session_jwt

      if (!jwt) {
        console.error('No JWT found in OAuth response:', stytchResponse)
        return {
          success: false,
          error: 'No session token received from authentication',
        }
      }

      // Store JWT in localStorage
      this.storeJWT(jwt)

      // For OAuth, try login first
      let backendResult = await this.registerLogin(jwt)

      // If login fails with user not found, try signup
      if (!backendResult.success) {
        console.log('Login failed, attempting signup for OAuth user')
        backendResult = await this.registerSignup(jwt)
      }

      if (!backendResult.success) {
        console.warn('Backend registration failed for OAuth, but local auth succeeded')
      }

      return backendResult
    } catch (error) {
      console.error('Error handling OAuth success:', error)
      return {
        success: false,
        error: error.message || 'Failed to complete OAuth authentication',
      }
    }
  }
}

export default new AuthService()
